import { definePreset, kf, rad, timing } from "./helpers";

import type { AnimationPreset } from "./types";

/**
 * 3D & projection presets. The renderer is 2D, so depth is faked with the
 * transform channels: the classic isometric projection is
 * rotate(-t) · skewX(t) · scaleY(cos t) — the same trick CSS 2.5D UIs use —
 * and a scaleX sweep through -1 with a perspective skew wobble reads as a
 * Y-axis spin.
 */

/** The three channel values of an isometric projection at tilt `t` radians
 * (sign picks the side the top face leans toward). */
const isoPose = (t: number, sign: number) => ({
  angle: -t * sign,
  skew: t * sign,
  scale: Math.cos(t),
});

const TILT_SCHEMA = {
  key: "degrees",
  label: "Tilt °",
  min: 5,
  max: 60,
  step: 1,
};
const DIRECTION_2SIDED = {
  key: "direction",
  label: "Direction",
  min: 0,
  max: 1,
  step: 1,
};

/** The panel's direction dropdown has 4 options; a projection only has two
 * sides, so left/up lean left and right/down lean right. */
const dirSign = (direction: number | undefined) =>
  direction === 1 || direction === 3 ? 1 : -1;

/** Per-spin keyframes for a fake perspective spin: scaleX sweeps through
 * edge-on (0) and mirrored (-1) while a skew wobble peaks when edge-on. */
const spinFrames = (spins: number, wobble: number) => {
  const scale = [1];
  const skew = [0];
  for (let i = 0; i < spins; i++) {
    scale.push(0, -1, 0, 1);
    skew.push(wobble, 0, -wobble, 0);
  }
  return { scale, skew };
};

export const threeDPresets: AnimationPreset[] = [
  definePreset(
    "isometricIn",
    "Isometric In",
    "threeD",
    {
      params: { degrees: 30, direction: 0, scale: 0.7 },
      schema: [
        TILT_SCHEMA,
        DIRECTION_2SIDED,
        { key: "scale", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "power3.out",
    },
    (tl, target, _element, cfg) => {
      const pose = isoPose(
        rad(cfg.params.degrees ?? 30),
        dirSign(cfg.params.direction),
      );
      const from = cfg.params.scale ?? 0.7;
      tl.fromTo(
        target,
        {
          angleDelta: pose.angle,
          skewX: pose.skew,
          scaleY: pose.scale * from,
          scaleX: from,
          opacity: 0,
        },
        {
          angleDelta: 0,
          skewX: 0,
          scaleY: 1,
          scaleX: 1,
          opacity: 1,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "isometricTilt",
    "Isometric Tilt",
    "threeD",
    {
      params: { degrees: 30, direction: 0 },
      schema: [TILT_SCHEMA, DIRECTION_2SIDED],
      duration: 1600,
    },
    (tl, target, _element, cfg) => {
      const pose = isoPose(
        rad(cfg.params.degrees ?? 30),
        dirSign(cfg.params.direction),
      );
      tl.to(
        target,
        kf(
          cfg,
          {
            angleDelta: [0, pose.angle, pose.angle, 0],
            skewX: [0, pose.skew, pose.skew, 0],
            scaleY: [1, pose.scale, pose.scale, 1],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "isometricView",
    "Isometric View",
    "threeD",
    {
      params: { degrees: 30, lean: 0, rotate: 0, zoom: 1 },
      schema: [
        { ...TILT_SCHEMA, min: 0 },
        {
          key: "lean",
          label: "Lean",
          min: 0,
          max: 1,
          step: 1,
          options: ["Left", "Right"],
        },
        { key: "rotate", label: "Rotate °", min: -180, max: 180, step: 5 },
        { key: "zoom", label: "Zoom", min: 0.5, max: 2, step: 0.05 },
      ],
      easing: "power2.inOut",
    },
    // unlike isometricIn/Tilt this ends IN the projection and holds it —
    // the element stays in isometric view after the clip
    (tl, target, _element, cfg) => {
      const pose = isoPose(
        rad(cfg.params.degrees ?? 30),
        (cfg.params.lean ?? 0) === 1 ? 1 : -1,
      );
      const zoom = cfg.params.zoom ?? 1;
      tl.to(
        target,
        {
          angleDelta: pose.angle + rad(cfg.params.rotate ?? 0),
          skewX: pose.skew,
          scaleY: pose.scale * zoom,
          scaleX: zoom,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "coinSpin",
    "Coin Spin",
    "threeD",
    {
      params: { spins: 2, wobble: 8 },
      schema: [
        { key: "spins", label: "Spins", min: 1, max: 10, step: 1 },
        { key: "wobble", label: "Wobble °", min: 0, max: 30, step: 1 },
      ],
      duration: 1500,
      easing: "power1.inOut",
    },
    (tl, target, _element, cfg) => {
      const spins = Math.max(1, Math.round(cfg.params.spins ?? 2));
      const { scale, skew } = spinFrames(spins, rad(cfg.params.wobble ?? 8));
      tl.to(target, kf(cfg, { scaleX: scale, skewY: skew }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "flipSpin",
    "Flip Spin",
    "threeD",
    {
      params: { spins: 2, wobble: 8 },
      schema: [
        { key: "spins", label: "Spins", min: 1, max: 10, step: 1 },
        { key: "wobble", label: "Wobble °", min: 0, max: 30, step: 1 },
      ],
      duration: 1500,
      easing: "power1.inOut",
    },
    (tl, target, _element, cfg) => {
      const spins = Math.max(1, Math.round(cfg.params.spins ?? 2));
      const { scale, skew } = spinFrames(spins, rad(cfg.params.wobble ?? 8));
      tl.to(target, kf(cfg, { scaleY: scale, skewX: skew }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "perspectiveSway",
    "Perspective Sway",
    "threeD",
    {
      params: { degrees: 12 },
      schema: [{ key: "degrees", label: "Degrees", min: 1, max: 45, step: 1 }],
      duration: 1600,
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 12;
      tl.to(
        target,
        kf(
          cfg,
          {
            skewY: [0, d, -d * 0.6, d * 0.3, -d * 0.15, 0].map(rad),
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "depthPop",
    "Depth Pop",
    "threeD",
    {
      params: { scale: 1.15, lift: 10 },
      schema: [
        { key: "scale", label: "Scale", min: 1, max: 2, step: 0.05 },
        { key: "lift", label: "Lift", min: 0, max: 100, step: 2 },
      ],
      duration: 700,
    },
    (tl, target, _element, cfg) => {
      const s = cfg.params.scale ?? 1.15;
      const lift = cfg.params.lift ?? 10;
      tl.to(
        target,
        kf(
          cfg,
          {
            scaleX: [1, s, 1],
            scaleY: [1, s, 1],
            dy: [0, -lift, 0],
            brightness: [1, 1.15, 1],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
];
